// src/components/client/ClientDashboard.jsx
import React, { useState, useMemo } from "react";
import { LayoutGrid, PlusCircle, UserCircle2, Headphones } from "lucide-react";
import toast from "react-hot-toast";
import AppShell from "../layout/AppShell";
import TripCard from "../trips/TripCard";
import {
  Card, Button, Input, Select, Textarea, Avatar,
  StatCard, EmptyState, Spinner, T
} from "../ui";
import { useAuth } from "../../contexts/AuthContext";
import { useClientTrips } from "../../hooks/useTrips";
import { useCaptains } from "../../hooks/useUsers";
import { createTrip } from "../../services/tripService";
import { updateClientProfile } from "../../services/userService";
import { useSettings } from "../../contexts/SettingsContext";
import { ClientSupportChat } from "../support/SupportChat";
import {
  Map, PlusSquare, User, MessageCircle,
  MapPin, Calendar, Clock, Users, FileText,
  Send, ShieldCheck, CreditCard, CheckCircle2, Navigation
} from "lucide-react";

// ── MODERN INPUT COMPONENT ─────────────────────────────────────
// هذا المكون يحل مشكلة التعريف ويضيف الشكل الاحترافي لكل الحقول
const ModernInput = ({ label, icon, error, type = "text", as = "input", children, ...props }) => {
  const [focused, setFocused] = useState(false);
  const isSelect = as === "select";
  const isTextarea = as === "textarea";

  const fieldStyles = {
    width: "100%",
    padding: isTextarea ? "14px 45px 14px 15px" : "14px 45px 14px 15px",
    borderRadius: "16px",
    fontSize: "15px",
    fontWeight: "600",
    border: "2px solid",
    borderColor: error ? "#EF4444" : (focused ? "#7C3AED" : "#F1F5F9"),
    background: focused ? "#FFFFFF" : "#F8FAFC",
    boxShadow: focused ? "0 10px 20px -5px rgba(124, 58, 237, 0.15)" : "none",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    minHeight: isTextarea ? "100px" : "auto",
    appearance: isSelect ? "none" : "auto", // إزالة سهم السلكت الافتراضي لستايل أنظف
  };

  return (
    <div style={{ marginBottom: 15, textAlign: "right" }}>
      {label && (
        <label style={{ display: "block", fontSize: 13, fontWeight: 800, color: "#475569", marginBottom: 8, marginRight: 5 }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: isTextarea ? "flex-start" : "center" }}>
        <div style={{
          position: "absolute", right: 15, zIndex: 10,
          top: isTextarea ? "16px" : "50%",
          transform: isTextarea ? "none" : "translateY(-50%)",
          color: focused ? "#7C3AED" : "#94A3B8",
          transition: "all 0.3s ease", display: "flex", alignItems: "center"
        }}>
          {icon}
        </div>

        {isSelect ? (
          <select {...props} style={fieldStyles} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
            {children}
          </select>
        ) : isTextarea ? (
          <textarea {...props} style={fieldStyles} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        ) : (
          <input type={type} {...props} style={fieldStyles} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        )}
      </div>
      {error && <span style={{ color: "#EF4444", fontSize: 11, fontWeight: 700, marginTop: 5, display: "block" }}>{error}</span>}
    </div>
  );
};

// ── MAIN DASHBOARD ─────────────────────────────────────────────
export default function ClientDashboard() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("trips");
  const { data: trips = [], loading } = useClientTrips(user?.id);
  const { data: captains = [] } = useCaptains();
  const settings = useSettings();

  const captainProfiles = useMemo(
    () => Object.fromEntries(captains.map(c => [c.id, c])),
    [captains]
  );

  const tabs = [
    {
      key: "trips",
      label: "رحلاتي",
      icon: <LayoutGrid size={22} strokeWidth={2.5} color="#3B82F6" fill="#3B82F622" />
      // تم تغيير Map إلى LayoutGrid لتعطي إيحاءً بجدول أو قائمة الرحلات المنظمة
    },
    {
      key: "new",
      label: "رحلة جديدة",
      icon: <PlusCircle size={22} strokeWidth={2.5} color="#10B981" fill="#10B98122" />
      // تم توحيد اللون الأخضر (Success) واستخدام PlusCircle بشكل دائري ألطف
    },
    {
      key: "profile",
      label: "حسابي",
      icon: <UserCircle2 size={22} strokeWidth={2.5} color="#8B5CF6" fill="#8B5CF622" />
      // تم تغيير User إلى UserCircle2 وتصحيح لون الـ fill ليكون بنفسجي متناسق
    },
    {
      key: "support",
      label: "الدعم",
      icon: <Headphones size={22} strokeWidth={2.5} color="#F59E0B" fill="#F59E0B22" />
      // تم تغيير MessageCircle إلى Headphones لتعطي انطباعاً بوجود "مركز مساعدة" مباشر
    },
  ];

  return (
    <AppShell tabs={tabs} activeTab={tab} onTabChange={setTab}>
      {tab === "trips" && (
        <div style={{ animation: "fadeIn 0.5s ease" }}>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>
            رحلاتي <span style={{ fontSize: 14, color: T.muted, fontWeight: 400 }}>({trips.length})</span>
          </h2>
          {loading ? <Spinner /> : trips.length === 0
            ? <EmptyState
              icon="🚗" title="لا توجد رحلات بعد"
              sub="اطلب رحلتك الأولى الآن وسيتواصل معك الكباتن بعروضهم"
              action={() => setTab("new")} actionLabel="➕ طلب رحلة جديدة"
            />
            : trips.map(t => <TripCard key={t.id} trip={t} captainProfiles={captainProfiles} />)
          }
        </div>
      )}

      {tab === "new" && (
        <NewTripForm user={user} settings={settings} onSuccess={() => setTab("trips")} />
      )}

      {tab === "profile" && (
        <ClientProfile user={user} trips={trips} refreshUser={refreshUser} />
      )}

      {tab === "support" && (
        <div style={{ animation: "fadeIn 0.5s ease" }}>
          <h2 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800 }}>💬  خدمة العملاء والدعم </h2>
          <p style={{ margin: "0 0 14px", fontSize: 13, color: T.muted }}>  يمكنك التواصل مع فريق راديو  عبر الشات او الاتصال    </p>
          <div style={{ background: "white", borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden" }}>
            <ClientSupportChat settings={settings} />
          </div>
        </div>
      )}
    </AppShell>
  );
}

// ── NEW TRIP FORM (MODERNIZED) ─────────────────────────────────
function NewTripForm({ user, settings, onSuccess }) {
  const [f, setF] = useState({ from: "", to: "", date: "", time: "", passengers: 1, notes: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const validate = () => {
    const e = {};
    if (!f.from) e.from = "مطلوب";
    if (!f.to) e.to = "مطلوب";
    if (!f.date) e.date = "مطلوب";
    if (!f.time) e.time = "مطلوب";
    if (f.from && f.to && f.from.trim() === f.to.trim()) e.to = "الوجهة يجب أن تختلف عن الانطلاق";
    const today = new Date().toISOString().split("T")[0];
    if (f.date && f.date < today) e.date = "لا يمكن حجز تاريخ في الماضي";
    return e;
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    setBusy(true);
    try {
      await createTrip({ ...f, clientId: user.id, clientName: user.name, clientPhone: user.phone || null });
      toast.success("🚀 تم إرسال طلب الرحلة! انتظر عروض الكباتن");
      onSuccess();
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const set = (k, v) => {
    setF(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: undefined }));
  };

  const steps = [
    { n: "1", icon: <MapPin size={16} />, text: " اطلب رحلة", c: "#3B82F6", bg: "#EFF6FF" },
    { n: "2", icon: <CreditCard size={16} />, text: "اختر العرض", c: "#F97316", bg: "#FFF7ED" },
    { n: "3", icon: <ShieldCheck size={16} />, text: "ادفع الديبوزيت", c: "#10B981", bg: "#F0FDF4" },
    { n: "4", icon: <CheckCircle2 size={16} />, text: "مشوارك فى امان ", c: "#7C3AED", bg: "#F5F3FF" },
  ];

  return (
    <div style={{ animation: "fadeIn 0.5s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#1E293B" }}>🚗 طلب رحلة جديدة</h2>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: T.muted }}>أدخل تفاصيل مشوارك وسيتواصل معك الكباتن بعروضهم</p>
      </div>

      <Card style={{ borderRadius: 28, padding: 24, border: "none", boxShadow: "0 10px 40px rgba(0,0,0,0.04)" }}>
        {/* Route Section */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5, position: "relative" }}>
          <ModernInput
            label=" هاتتحرك من ؟"
            placeholder="مكان التحرك...   "
            icon={<MapPin size={20} strokeWidth={2.5} color="#10B981" />}
            value={f.from}
            onChange={e => set("from", e.target.value)}
            error={errors.from}
          />

          <div style={{
            position: "absolute", right: 24, top: 60, bottom: 50,
            width: 2, borderRight: "2px dashed #E2E8F0", zIndex: 1
          }} />

          <ModernInput
            label=" رايح فين ؟"
            placeholder="  الى..."
            icon={<Navigation size={20} strokeWidth={2.5} color="#EF4444" />}
            value={f.to}
            onChange={e => set("to", e.target.value)}
            error={errors.to}
          />
        </div>

        {/* Date & Time */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
          <ModernInput
            label="التاريخ" type="date"
            icon={<Calendar size={18} strokeWidth={2.5} />}
            value={f.date} onChange={e => set("date", e.target.value)} error={errors.date}
          />
          <ModernInput
            label="الوقت" type="time"
            icon={<Clock size={18} strokeWidth={2.5} />}
            value={f.time} onChange={e => set("time", e.target.value)} error={errors.time}
          />
        </div>

        {/* Passengers */}
        <div style={{ marginTop: 10 }}>
          <ModernInput
            label="عدد الركاب"
            as="select"
            icon={<Users size={18} strokeWidth={2.5} />}
            value={f.passengers}
            onChange={e => set("passengers", +e.target.value)}
          >
            {[1, 2, 3, 4, 5, 6].map(n => <option key={n} value={n}>{n} {n === 1 ? 'راكب' : 'ركاب'}</option>)}
          </ModernInput>
        </div>

        {/* Notes */}
        <div style={{ marginTop: 10 }}>
          <ModernInput
            label="تفاصيل المشوار"
            as="textarea"
            placeholder="تفاصيل المشوار لمساعدة الكابتن فى تحديد السعر (سيشن مثلاً - وقت الانتظار)..."
            icon={<FileText size={18} strokeWidth={2.5} />}
            value={f.notes}
            onChange={e => set("notes", e.target.value)}
          />
        </div>

        {/* Stepper */}
        <div style={{ marginTop: 25, padding: "16px", background: "#F8FAFC", borderRadius: 20, border: "1px dashed #E2E8F0" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#1E293B", marginBottom: 12 }}>⚡ كيف يعمل رايدو؟</div>
          <div style={{ display: "flex", overflowX: "auto", gap: 10, paddingBottom: 5, scrollbarWidth: "none" }}>
            {steps.map((step) => (
              <div key={step.n} style={{ minWidth: 100, padding: 10, background: "white", borderRadius: 12, border: "1px solid #F1F5F9" }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: step.bg, color: step.c, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 6 }}>
                  {step.icon}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700 }}>{step.text}</div>
              </div>
            ))}
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 11, color: "#B45309", background: "#FFFBEB", padding: "6px 10px", borderRadius: 8 }}>
            💡 تأكيد الحجز بديبوزيت <b>{settings.depositFixed || 100} ج.م</b> يُدفع عبر InstaPay/محفظة.
          </p>
        </div>

        <Button full size="lg" loading={busy} onClick={submit} style={{ marginTop: 20, height: 58, borderRadius: 18, fontSize: 17, fontWeight: 800 }}>
          🚀 إرسال طلب الرحلة
        </Button>
      </Card>
    </div>
  );
}

// ── CLIENT PROFILE ─────────────────────────────────────────────
function ClientProfile({ user, trips, refreshUser }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("الاسم مطلوب"); return; }
    setBusy(true);
    try {
      await updateClientProfile(user.id, { name: name.trim(), phone: phone.trim() });
      await refreshUser();
      toast.success("✓ تم تحديث الملف الشخصي");
      setEditing(false);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const stats = {
    completed: trips.filter(t => t.status === "completed").length,
    cancelled: trips.filter(t => t.status === "cancelled").length
  };

  return (
    <div style={{ animation: "fadeIn 0.5s ease" }}>
      <Card style={{ textAlign: "center", marginBottom: 16, borderRadius: 24 }}>
        <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
          <Avatar name={user?.name} size={80} color={T.success} />
        </div>
        <div style={{ marginTop: 14, fontSize: 22, fontWeight: 900 }}>{user?.name}</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>{user?.email}</div>
        <div style={{ fontSize: 13, color: T.muted }}>{user?.phone || "لا يوجد رقم مسجل"}</div>
        <Button variant="ghost" size="sm" style={{ marginTop: 14 }}
          onClick={() => { setName(user?.name || ""); setPhone(user?.phone || ""); setEditing(!editing); }}>
          {editing ? "إلغاء التعديل" : "✏️ تعديل البيانات"}
        </Button>
      </Card>

      {editing && (
        <Card style={{ marginBottom: 16, border: `2px solid ${T.success}33` }}>
          <h4 style={{ margin: "0 0 14px", fontWeight: 700 }}>تعديل الملف الشخصي</h4>

          <ModernInput
            label="الاسم الكامل"
            value={name}
            onChange={e => setName(e.target.value)}
            icon={<User size={18} />}
          />

          {/* تعديل حقل رقم التليفون ليكون للقراءة فقط */}
          <ModernInput
            label="رقم التليفون (لا يمكن تغييره)"
            type="tel"
            value={phone}
            readOnly
            disabled
            style={{ opacity: 0.7, cursor: "not-allowed" }}
            icon={<Users size={18} />}
          />

          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Button variant="success" style={{ flex: 1 }} loading={busy} onClick={save}>💾 حفظ التغييرات</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>إلغاء</Button>
          </div>
        </Card>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <StatCard label="الرحلات" value={trips.length} icon="🏁" color={T.info} />
        <StatCard label="مكتملة" value={stats.completed} icon="✅" color={T.success} />
        <StatCard label="ملغاة" value={stats.cancelled} icon="✕" color={T.danger} />
      </div>
    </div>
  );
}